import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, Building2, Loader2, Eye, EyeOff, Shield } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  // Login states
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  
  // 2FA states
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");

  useEffect(() => {
    // Check if already authenticated
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (response.data) {
          navigate('/dashboard');
        }
      } catch (error) {
        // Not authenticated, stay on login page
      }
    };
    checkAuth();

    // Handle OAuth callback
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    
    if (code && state) {
      handleOAuthCallback(code, state);
    }
  }, [navigate, searchParams]);

  const handleOAuthCallback = async (code, state) => {
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.get(
        `${API}/auth/google/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`,
        { withCredentials: true }
      );
      
      if (response.data.success) {
        navigate('/dashboard');
      } else {
        setError("Authentication failed. Please try again.");
      }
    } catch (error) {
      console.error("OAuth callback error:", error);
      setError(error.response?.data?.detail || "Authentication failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.get(`${API}/auth/google/login`);
      
      if (response.data.mock) {
        setError(response.data.message);
        setLoading(false);
        return;
      }
      
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error("Google login error:", error);
      setError("Failed to initiate Google login. Please try again.");
      setLoading(false);
    }
  };

  const handleEmailPasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/customer/auth/login`, {
        email,
        password
      }, { withCredentials: true });

      if (response.data.success) {
        if (response.data.requires_2fa) {
          // 2FA is required
          setRequires2FA(true);
          setTempToken(response.data.temp_token);
        } else {
          // No 2FA, login successful
          navigate('/dashboard');
        }
      } else {
        setError(response.data.error || "Login failed. Please check your credentials.");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.response?.data?.error || err.response?.data?.detail || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!twoFactorCode || twoFactorCode.length < 6) {
      setError("Please enter a valid verification code");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/customer/auth/verify-2fa-login`, {
        temp_token: tempToken,
        code: twoFactorCode
      }, { withCredentials: true });

      if (response.data.success) {
        navigate('/dashboard');
      } else {
        setError(response.data.error || "Invalid verification code");
      }
    } catch (err) {
      console.error("2FA verification error:", err);
      setError(err.response?.data?.error || err.response?.data?.detail || "Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setRequires2FA(false);
    setTempToken("");
    setTwoFactorCode("");
    setError("");
  };

  // 2FA Verification Screen
  if (requires2FA) {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="glass-heavy rounded-3xl p-8 border border-white/10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-blue-400" />
              </div>
              <h1 className="text-3xl font-heading font-semibold text-white mb-2">
                Two-Factor Authentication
              </h1>
              <p className="text-slate-400">
                Enter the verification code from your authenticator app or check your email/SMS
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleVerify2FA} className="space-y-6">
              <div>
                <Label htmlFor="2fa-code" className="text-white">Verification Code</Label>
                <Input
                  id="2fa-code"
                  type="text"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  maxLength={6}
                  className="mt-1 bg-white/5 border-white/10 text-white text-center text-2xl tracking-widest placeholder:text-slate-500"
                  required
                  autoFocus
                />
                <p className="text-slate-400 text-xs mt-1">
                  Enter the 6-digit code from your authenticator app, email, or SMS
                </p>
              </div>

              <Button
                type="submit"
                disabled={loading || twoFactorCode.length !== 6}
                className="w-full h-12 rounded-full gradient-active hover:opacity-90 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Verifying...
                  </>
                ) : (
                  "Verify & Sign In"
                )}
              </Button>

              <button
                type="button"
                onClick={handleBackToLogin}
                className="w-full text-slate-400 hover:text-white transition-colors text-sm"
              >
                ← Back to login
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Main Login Screen
  return (
    <div className="min-h-screen gradient-hero flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="glass-heavy rounded-3xl p-8 border border-white/10">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="w-12 h-12 rounded-xl gradient-active flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-heading font-semibold text-white">QuantumFlowSync</span>
            </div>
            <h1 className="text-3xl font-heading font-semibold text-white mb-2">Welcome back</h1>
            <p className="text-slate-300">Sign in to manage your workflows</p>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Email/Password Login Form */}
          <form onSubmit={handleEmailPasswordLogin} className="space-y-4 mb-6">
            <div>
              <Label htmlFor="email" className="text-white">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                required
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-white">Password</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <Link to="/forgot-password" className="text-blue-400 hover:text-blue-300 transition-colors">
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full h-12 rounded-full gradient-active hover:opacity-90 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-navy-500 px-2 text-slate-500">Or continue with</span>
            </div>
          </div>

          <div className="space-y-3">
            <Button 
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full h-12 rounded-full bg-white hover:bg-slate-100 text-navy-500 font-medium flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="google-login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google
                </>
              )}
            </Button>

            <Button 
              onClick={() => navigate('/login/sso')}
              variant="outline"
              disabled={loading}
              className="w-full h-12 rounded-full border-white/10 bg-white/5 hover:bg-white/10 text-white font-medium flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Building2 className="w-5 h-5" />
              SSO
            </Button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-slate-500 text-sm">
              By signing in, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>

          <div className="text-center mt-6">
            <p className="text-slate-400 text-sm">
              Don't have an account?{" "}
              <Link to="/signup" className="text-blue-400 hover:text-blue-300 transition-colors font-medium">
                Sign up
              </Link>
            </p>
          </div>
        </div>

        <div className="text-center mt-6 space-y-3">
          <button 
            onClick={() => navigate('/')} 
            className="text-slate-400 hover:text-white transition-colors text-sm block mx-auto"
          >
            ← Back to home
          </button>
        </div>
      </div>
    </div>
  );
}
